import { TRADE_EXTRA_DETAILS_PATH } from "common/constants";
import CancelTradeTrigger from "features/trades/CancelTrade/CancelTradeTrigger";
import React, { ReactNode, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteWithoutPath, getTradeExtraDetailsRoute } from "utils/routes";
import EditTradeExtraActions from "features/trades/EditTrade/EditTradeExtraActions";
import DrawerTitleLayout from "common/Drawers/DrawerTitleLayout";
import Button, { StyledDefaultButton } from "common/Buttons/Button";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import ShareAltOutlined from "@ant-design/icons/ShareAltOutlined";
import TextWithIcon from "common/Text/TextWithIcon";
import {
  GetTradeExtraInfoGeneralResponse,
  LanguageNameSpaces,
  TradeGraphData,
} from "@shared/types";
import { ValueOf } from "@shared/types/utils";
import { ICON_TYPES, TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import styled, { useTheme } from "styled-components";
import IconActionButton from "common/Buttons/IconActionButton";
import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import { openMessage, displayResultMessage } from "utils/misc";
import { getBotManagedTradeEditWarning } from "@shared/features/trades/common/constants";
import { useShareTradeResults } from "@shared/features/trades/common/hooks";
import ShareTradeTrigger from "features/trades/common/ShareTradeTrigger";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import AnimatedShareIcon from "common/Animations/AnimatedShareIcon";
import Tag from "common/Tags/Tag";
import Flex from "common/Boxes/Flex";
import DebouncedButton from "common/Buttons/DebouncedButton";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useExchangesMetadata,
  useSubUserPermissions,
} from "@shared/utils/hooks/misc.ts";
import { Pencil, Share2, Trash2 } from "lucide-react";
import { useBottomNavigator } from "utils/hooks/misc";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import NewLookDrawerTitle, {
  parseLeverage,
} from "features/trades/NewLook/NewLookDrawerTitle.tsx";

interface TradeDrawerTitleProps {
  tradeId: number;
  tradeExtraInfoGeneral?: GetTradeExtraInfoGeneralResponse;
  tab: ValueOf<typeof TRADE_EXTRA_DETAILS_TABS>;
  isLoading: boolean;
}

const TradeDrawerTitle = ({
  tradeId,
  tradeExtraInfoGeneral,
  tab,
  isLoading,
}: TradeDrawerTitleProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { ordersData, symbol, tradeMetadata, isBacktesting } =
    useContext(TradeDetailsContext)!;
  const {
    onSaveChangesClick,
    editTradeLoading,
    editTradeConfigurationLoading,
    valuesModified,
    isEdit,
    onCancelChangesClick,
  } = useContext(EditTradeContext) || {};
  const isOpen = tradeMetadata?.trade_open;
  const location = useLocation();
  const { historyPush } = useHistoryWithQuery();
  const { quotaModal, handleTriggerClick } = useQuotaModal({});
  const { registerBackButtonListener } = useBackButtonHandler();
  const { isReadOnly } = useSubUserPermissions();
  const { getIsFree } = useExchangesMetadata();
  const { baseShouldDisplay: isPhone } = useBottomNavigator();

  const {
    isShareResultsModalVisible,
    shareTradeImageUrl,
    shareTradeImageLoading,
    openShareResultsModal,
    closeShareResultsModal,
    imageTitle,
    getShareResultsErrorMessage,
  } = useShareTradeResults(displayResultMessage, openMessage);

  const isInvalid = !!getShareResultsErrorMessage(
    tradeMetadata?.source,
    !tradeMetadata?.client_is_valid
  );
  const showAnimation =
    !isInvalid &&
    (tradeExtraInfoGeneral?.data.graph_data as TradeGraphData)?.profit >=
      (tradeExtraInfoGeneral?.data.general["Leverage"] ? 10 : 5);

  const onCloseClick = () => {
    onCancelChangesClick?.(() => onEditClick(false));
    return true;
  };

  useEffect(() => {
    if (isEdit) {
      return registerBackButtonListener(onCloseClick);
    }
  }, [isEdit]);

  const onEditClick = (isEdit: boolean) => {
    const route = getRouteWithoutPath(
      location.pathname,
      TRADE_EXTRA_DETAILS_PATH
    );
    const nextTab =
      tab === TRADE_EXTRA_DETAILS_TABS.CONFIGURATION
        ? tab
        : TRADE_EXTRA_DETAILS_TABS.ORDERS;
    historyPush(getTradeExtraDetailsRoute(route, tradeId, nextTab, isEdit));
  };

  const editDisabledWarning = getBotManagedTradeEditWarning(
    ordersData?.data.source
  );
  const isEditDisabled = !!editDisabledWarning;
  const isInvalidAccount = !tradeMetadata?.client_is_valid;

  const handleEditTrade = () => {
    if (editDisabledWarning) {
      openMessage(t(editDisabledWarning), "warning");
    } else if (isInvalidAccount) {
      openMessage(t("warnings.inactive_account_edit"), "warning");
    } else {
      onEditClick(true);
    }
  };

  const onEditButtonClick = () => {
    if (getIsFree(tradeMetadata?.exchange)) {
      handleEditTrade();
    } else {
      handleTriggerClick({ ignoreQuotaCheck: true }).then((isExpired) => {
        if (isExpired) return;
        handleEditTrade();
      });
    }
  };

  let leftSideIcons: ReactNode;
  if (!isEdit && !isLoading) {
    leftSideIcons = (
      <>
        {isOpen && (
          <>
            {!isBacktesting && !isReadOnly && (
              <>
                <CancelTradeTrigger
                  trigger={
                    <IconActionButton
                      icon={
                        <LookIcon
                          classic={<DeleteOutlined />}
                          newLook={
                            <Trash2
                              size={16}
                              strokeWidth={NEW_LOOK_ICON_STROKE}
                            />
                          }
                        />
                      }
                      actionName={t("actions.close_trade")}
                    />
                  }
                  tradeId={tradeId}
                  showCloseOptions={
                    (tradeExtraInfoGeneral?.data?.graph_data?.entry_progress ||
                      0) > 0
                  }
                  accountIsValid={tradeMetadata?.client_is_valid}
                  symbol={symbol}
                  accountId={tradeMetadata?.client_id}
                />
                <IconActionButton
                  onClick={onEditButtonClick}
                  icon={
                    <LookIcon
                      classic={<EditOutlined />}
                      newLook={
                        <Pencil size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                      }
                    />
                  }
                  actionName={t("labels.edit_trade")}
                  disabled={isEditDisabled || isInvalidAccount}
                />
              </>
            )}
          </>
        )}
        {!isBacktesting && (
          <ShareTradeTrigger
            trigger={
              <StyledDefaultButton
                loading={shareTradeImageLoading}
                disabled={isInvalid}
                style={{ display: "flex", alignItems: "center" }}
              >
                {showAnimation ? (
                  <StyledAnimatedShareIcon />
                ) : (
                  <LookIcon
                    classic={<StyledShareAltOutlined />}
                    newLook={<NewLookShareIcon size={16} strokeWidth={1.9} />}
                  />
                )}
                {t("drawer.share_results")}
              </StyledDefaultButton>
            }
            visible={isShareResultsModalVisible}
            openModal={() =>
              openShareResultsModal(
                tradeId,
                symbol,
                tradeMetadata?.source,
                !tradeMetadata?.client_is_valid
              )
            }
            closeModal={closeShareResultsModal}
            shareTradeImageUrl={shareTradeImageUrl}
            imageTitle={imageTitle}
          />
        )}
        {quotaModal}
      </>
    );
  } else if (isOpen && isEdit) {
    leftSideIcons = (
      <Button
        onClick={onCloseClick}
        disabled={editTradeLoading || editTradeConfigurationLoading}
      >
        {t("actions.cancel")}
      </Button>
    );
  }

  const rightSideIcons = isOpen && isEdit && (
    <>
      {tab !== TRADE_EXTRA_DETAILS_TABS.CONFIGURATION && (
        <EditTradeExtraActions />
      )}
      <StyledButton
        onClick={onSaveChangesClick}
        loading={editTradeLoading || editTradeConfigurationLoading}
        $tradeValuesModified={valuesModified}
      >
        {t("actions.save")}
      </StyledButton>
    </>
  );

  const titleText = symbol
    ? t("drawer.title_with_symbol", {
        symbol,
        status: isOpen ? t("drawer.status_open") : t("drawer.status_closed"),
      })
    : undefined;

  const backtestingTag = isBacktesting && (
    <Tag color={theme.colors.primaryBlue} style={{ marginLeft: 8 }}>
      {t("drawer.backtesting")}
    </Tag>
  );

  const general = tradeExtraInfoGeneral?.data.general;
  const newLookTitle = (
    <NewLookDrawerTitle
      symbol={symbol}
      side={general?.["Long/Short"]}
      leverage={parseLeverage(general?.["Leverage"])}
      status={isOpen ? t("drawer.status_open") : t("drawer.status_closed")}
      chips={isBacktesting ? [t("drawer.backtesting")] : undefined}
      compact={isPhone}
    />
  );

  return (
    <DrawerTitleLayout
      leftActions={leftSideIcons}
      title={
        theme.isNewLook ? (
          newLookTitle
        ) : (
          <Flex $justifyContent="center">
            <TextWithIcon
              text={titleText}
              coinOrExchangeName={symbol}
              iconType={ICON_TYPES.COIN}
              justify={"center"}
            />
            {backtestingTag}
          </Flex>
        )
      }
      rightActions={rightSideIcons}
    />
  );
};

export default TradeDrawerTitle;

const StyledButton = styled(DebouncedButton)<{
  $tradeValuesModified?: boolean;
}>`
  ${(p) =>
    !p.$tradeValuesModified &&
    `
     &.ant-btn {
      cursor: unset;
      opacity: 0.4;
      &:hover {
        color: ${p.theme.colors.textColor};
        border: ${p.theme.borders.medium};
      }
    }
  `}
`;
const StyledShareAltOutlined = styled(ShareAltOutlined)`
  font-size: 16px;
  height: 16px;
  svg {
    color: ${(p) => p.theme.colors.primaryBlue}!important;
  }
`;
const NewLookShareIcon = styled(Share2)`
  color: ${(p) => p.theme.colors.primaryBlue};
`;
const StyledAnimatedShareIcon = styled(AnimatedShareIcon)`
  width: 28px;
  position: relative;
  left: -4px;
`;
